import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import useAdminData from "@/lib/useAdminData";
import { saveDraft, readDraft, clearDraft, draftKey } from "@/lib/draft";
import { AdminHeader, Card, Btn, Input, Textarea, Select, Toggle, ErrorBanner, Loading } from "@/components/admin/ui";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Upload, Star, ChevronLeft, ChevronRight, Link2 } from "lucide-react";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";

const emptyVariant = () => ({ id: null, sku: "", attributes: {}, price: 0, mrp: 0, stock: 0, images: [], active: true, weight: null, weightUnit: "g", weightInput: "", lengthCm: null, breadthCm: null, heightCm: null });

// ───────────────────────────── variant weight (grams) ─────────────────────────────
// Weights are entered in grams and stored with an explicit unit ("g"), which the
// shipping resolver converts exactly once when it asks Delhivery for a rate.
// A variant saved before this field existed has a weight but no unit; that value is
// NEVER reinterpreted — the input starts empty and the stored value is written back
// untouched unless the admin types a new one.

/** Error message for a weight input, or null when it is acceptable/empty. */
function weightInputError(raw) {
  const value = String(raw ?? "").trim();
  if (value === "") return null;                       // empty = keep fallback / stored value
  const number = Number(value);
  if (!Number.isFinite(number)) return "Weight must be a number in grams";
  if (number <= 0) return "Weight must be greater than 0 grams";
  if (number > 100000) return "Weight cannot exceed 100000 g (100 kg)";
  return null;
}

/** The value shown in the grams input for a stored variant. */
function variantWeightInput(v) {
  if (!v) return "";
  const unit = (v.weightUnit || "").toLowerCase();
  const grams = v.weight === null || v.weight === undefined || v.weight === "" ? null : Number(v.weight);
  if (grams === null || !Number.isFinite(grams) || grams <= 0) return "";
  if (unit === "g" || unit === "gram" || unit === "grams") return String(grams);
  if (unit === "kg" || unit === "kgs" || unit === "kilogram" || unit === "kilograms") return String(grams * 1000);
  return "";                                            // legacy value: do not reinterpret
}

/** Whether a stored variant has a weight we cannot label (no unit recorded). */
function isLegacyWeight(v) {
  const unit = (v?.weightUnit || "").toLowerCase();
  const known = ["g", "gram", "grams", "kg", "kgs", "kilogram", "kilograms"];
  const grams = Number(v?.weight);
  return !known.includes(unit) && Number.isFinite(grams) && grams > 0;
}

function dimensionInputError(raw, label) {
  const value = String(raw ?? "").trim();
  if (value === "") return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return `${label} must be a number in cm`;
  if (number <= 0) return `${label} must be greater than 0 cm`;
  if (!Number.isInteger(number)) return `${label} must be a whole number of cm for Delhivery`;
  return null;
}

const FORM_DEFAULTS = {
  name: "", categoryId: "", brandId: "", shortDescription: "", description: "",
  images: [], warranty: "", tags: [], status: "active",
  featured: false, trending: false, bestSeller: false, newArrival: false,
  hasVariants: false, variants: [emptyVariant()], specifications: [], lowStockThreshold: 5,
  // GST + return policy (optional; backend falls back to the store defaults).
  hsn: "", gstRate: null, returnPolicy: { allowed: false, windowDays: null, conditions: "" },
};

function parseTags(value) {
  const tags = Array.isArray(value) ? value : String(value || "").split(",");
  const seen = new Set();
  return tags.reduce((result, tag) => {
    const trimmed = String(tag || "").trim();
    const key = trimmed.toLocaleLowerCase();
    if (trimmed && !seen.has(key)) {
      seen.add(key);
      result.push(trimmed);
    }
    return result;
  }, []);
}

// Idempotency key for a create. If the save actually succeeded but its response was
// lost (network drop), clicking Save again re-sends the same key and the backend
// returns the product it already created instead of creating a duplicate.
function newRequestId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `prod-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function ProductForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [f, setF] = useState(FORM_DEFAULTS);
  const [version, setVersion] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [draftInfo, setDraftInfo] = useState(null);
  const requestIdRef = useRef(newRequestId());
  const touchedRef = useRef(false);
  const dKey = draftKey("product", id);

  const cats = useAdminData(() => api.get("/admin/categories").then((r) => r.data.categories));
  const brands = useAdminData(() => api.get("/admin/brands").then((r) => r.data.brands));
  const product = useAdminData(() => api.get(`/admin/products/${id}`).then((r) => r.data.product));
  // Store-level weight assumptions (unit for legacy values + fallback weight) so the
  // form can say exactly what shipping will use when a variant has no weight.
  const shipping = useAdminData(() => api.get("/admin/shipping").then((r) => r.data.provider));
  const productData = product.data;
  const productError = product.error;
  const reloadCats = cats.reload;
  const reloadBrands = brands.reload;
  const reloadProduct = product.reload;
  const reloadShipping = shipping.reload;

  useEffect(() => { reloadCats(); reloadBrands(); reloadShipping(); }, [reloadCats, reloadBrands, reloadShipping]);
  useEffect(() => { if (editing) reloadProduct(); }, [editing, reloadProduct, id]);

  // A failed product fetch must show an error + Retry — NOT an empty form, which
  // would invite overwriting the record with blank values.
  useEffect(() => {
    if (!productData) return;
    setF({
      ...productData, images: productData.images || [], tags: productData.tags || [],
      specifications: productData.specifications || [],
      variants: productData.variants?.length
        ? productData.variants.map((v) => ({ ...v, weightInput: variantWeightInput(v) }))
        : [emptyVariant()],
    });
    setVersion(productData.version ?? 1);
  }, [productData]);

  // Recover unsaved work from this tab (never from another device/browser session).
  useEffect(() => {
    const draft = readDraft(dKey);
    if (draft && draft.value?.fields) setDraftInfo(draft);
  }, [dKey]);

  // Debounced draft snapshot while the admin is actively editing.
  useEffect(() => {
    if (!touchedRef.current) return undefined;
    const t = setTimeout(() => saveDraft(dKey, { fields: f, version }), 800);
    return () => clearTimeout(t);
  }, [f, version, dKey]);

  const set = (k, v) => { touchedRef.current = true; setF((s) => ({ ...s, [k]: v })); };

  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of list) {
        if (!["image/jpeg", "image/png"].includes(file.type)) { toast.error(`${file.name}: only JPG/PNG allowed`); continue; }
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: exceeds 10 MB`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("productId", id || "tmp");
        const { data } = await api.post("/admin/uploads/product-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      if (uploaded.length) { setF((s) => ({ ...s, images: [...s.images, ...uploaded] })); toast.success(`${uploaded.length} image(s) uploaded`); }
    } catch (e) {
      // Fields and already-uploaded image references are left untouched.
      setSaveError({ message: apiError(e), status: e?.response?.status ?? null });
      toast.error(apiError(e));
    }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const setPrimary = (i) => { touchedRef.current = true; setF((s) => { const arr = [...s.images]; const [x] = arr.splice(i, 1); arr.unshift(x); return { ...s, images: arr }; }); };
  const moveImg = (i, dir) => { touchedRef.current = true; setF((s) => { const arr = [...s.images]; const j = i + dir; if (j < 0 || j >= arr.length) return s; [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...s, images: arr }; }); };
  const removeImg = (i) => { touchedRef.current = true; setF((s) => ({ ...s, images: s.images.filter((_, x) => x !== i) })); };
  const addUrl = () => { if (!urlInput.trim()) return; touchedRef.current = true; setF((s) => ({ ...s, images: [...s.images, urlInput.trim()] })); setUrlInput(""); };

  const setVariant = (i, k, v) => { touchedRef.current = true; setF((s) => { const vs = [...s.variants]; vs[i] = { ...vs[i], [k]: v }; return { ...s, variants: vs }; }); };
  const setVariantAttr = (i, key, val) => { touchedRef.current = true; setF((s) => { const vs = [...s.variants]; vs[i] = { ...vs[i], attributes: { ...vs[i].attributes, [key]: val } }; return { ...s, variants: vs }; }); };

  // Weight in grams (validated as it is typed, so the message is immediate).
  const setVariantWeight = (i, raw) => {
    touchedRef.current = true;
    setF((s) => {
      const vs = [...s.variants];
      vs[i] = { ...vs[i], weightInput: raw, weightError: weightInputError(raw) };
      return { ...s, variants: vs };
    });
  };

  const shippingUnit = shipping.data?.weightUnit || "kg";
  const shippingFallback = shipping.data?.defaultWeightGrams ?? null;

  /** What shipping currently does for this variant, in plain language. */
  const weightHint = (v) => {
    const typed = String(v.weightInput ?? "").trim();
    if (typed !== "") return "Used for the Delhivery shipping charge of this variant's parcels (sent in grams).";
    if (isLegacyWeight(v)) {
      const grams = Number(v.weight) * (shippingUnit === "kg" ? 1000 : 1);
      return `Stored legacy weight ${v.weight} — read as ${grams} g (store unit: ${shippingUnit}). `
        + "Enter a weight in grams above to set it explicitly; leaving it blank keeps this value unchanged.";
    }
    if (v.weight !== null && v.weight !== undefined && v.weight !== "") {
      return "Weight is stored but has no unit recorded — shipping uses the store fallback. Enter grams to set it explicitly.";
    }
    return shippingFallback
      ? `No weight set — Delhivery rates use the store fallback of ${shippingFallback} g per unit.`
      : "No weight set — Delhivery rates use the store's fallback weight per unit.";
  };

  const save = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (saving) return;
    if (!f.name.trim()) { toast.error("Product name is required"); return; }
    // Weight validation: a bad value is shown against the field instead of being
    // silently turned into the fallback weight.
    const invalid = f.variants
      .map((v, i) => ({ i, message: weightInputError(v.weightInput) }))
      .filter((x) => x.message);
    if (invalid.length) {
      toast.error(`Variant ${invalid[0].i + 1}: ${invalid[0].message}`);
      return;
    }
    const invalidDimension = f.variants
      .flatMap((v, i) => ["lengthCm", "breadthCm", "heightCm"]
        .map((field) => ({ i, message: dimensionInputError(v[field], field.replace("Cm", "")) }))
        .filter((x) => x.message))[0];
    if (invalidDimension) {
      toast.error(`Variant ${invalidDimension.i + 1}: ${invalidDimension.message}`);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        ...f,
        tags: parseTags(f.tags),
        variants: f.variants.map((v) => {
          // `weightInput`/`weightError` are form-only and never sent.
          const { weightInput, weightError, ...stored } = v;
          const typed = String(weightInput ?? "").trim();
          const weight = typed === ""
            // Left blank: keep whatever is stored (including a legacy value with no
            // unit) so nothing is erased or reinterpreted.
            ? (stored.weight === undefined ? null : stored.weight)
            : Number(typed);
          const weightUnit = typed === ""
            ? (stored.weightUnit ?? null)
            : "g";
          return {
            ...stored,
            price: Number(v.price), mrp: Number(v.mrp), stock: Number(v.stock),
            weight: weight === null || weight === "" ? null : Number(weight),
            weightUnit,
            lengthCm: stored.lengthCm === null || stored.lengthCm === undefined || String(stored.lengthCm).trim() === ""
              ? null : Number(stored.lengthCm),
            breadthCm: stored.breadthCm === null || stored.breadthCm === undefined || String(stored.breadthCm).trim() === ""
              ? null : Number(stored.breadthCm),
            heightCm: stored.heightCm === null || stored.heightCm === undefined || String(stored.heightCm).trim() === ""
              ? null : Number(stored.heightCm),
          };
        }),
        // Conflicts with the other admin session are detected instead of silently
        // overwriting its work; a create retry is de-duplicated by request id.
        ...(editing ? { expectedVersion: version } : { clientRequestId: requestIdRef.current }),
      };
      if (editing) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      clearDraft(dKey);
      touchedRef.current = false;
      toast.success(editing ? "Product updated" : "Product created");
      navigate("/admin/products");
    } catch (err) {
      // Nothing is reset and the form is NOT closed: every field, image reference and
      // selection stays exactly as entered so the admin can retry (after signing in
      // again if the session really expired).
      setSaveError({ message: apiError(err), status: err?.response?.status ?? null });
      toast.error(apiError(err));
    }
    finally { setSaving(false); }
  };

  const reloadLatest = async () => {
    if (!editing) return;
    if (!window.confirm("Discard your unsaved changes and load the latest saved version of this product?")) return;
    const res = await reloadProduct();
    if (res.ok) {
      setSaveError(null);
      setDraftInfo(null);
      clearDraft(dKey);
      touchedRef.current = false;
      toast.success("Loaded the latest saved version");
    }
  };

  const restoreDraft = () => {
    const fields = draftInfo?.value?.fields;
    if (!fields) { setDraftInfo(null); return; }
    touchedRef.current = true;
    setF({ ...FORM_DEFAULTS, ...fields });
    // Keep the version the draft was written against: if the record changed since,
    // the save is refused with a clear conflict message instead of overwriting it.
    setVersion(draftInfo.value.version ?? version);
    setDraftInfo(null);
    toast.success("Unsaved changes restored");
  };
  const discardDraft = () => { clearDraft(dKey); setDraftInfo(null); };

  // A failed product load must never render a blank form that could overwrite the
  // real record: show an error with Retry instead.
  if (editing && !productData && product.loading) return <Loading text="Loading product…" />;
  if (editing && !productData) {
    return (
      <div>
        <button type="button" onClick={() => navigate("/admin/products")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-3"><ArrowLeft className="h-4 w-4" /> Back</button>
        <AdminHeader title="Edit Product" />
        <ErrorBanner
          testid="pf-load-error"
          message={productError?.message || "This product could not be loaded."}
          onRetry={() => { setSaveError(null); reloadProduct(); }}
        />
      </div>
    );
  }

  return (
    <form onSubmit={save}>
      <button type="button" onClick={() => navigate("/admin/products")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-3"><ArrowLeft className="h-4 w-4" /> Back</button>
      <AdminHeader title={editing ? "Edit Product" : "New Product"}
        action={<Btn variant="accent" type="submit" loading={saving} data-testid="save-product-btn">{saveError ? "Retry Save" : "Save Product"}</Btn>} />

      {saveError && (
        <ErrorBanner
          className="mb-4"
          testid="pf-save-error"
          message={saveError.message}
          action={editing ? (
            <Btn type="button" variant="outline" onClick={reloadLatest} data-testid="pf-load-latest">Reload latest</Btn>
          ) : null}
        />
      )}

      {draftInfo && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-3 flex-wrap" data-testid="pf-draft-banner">
          <p className="text-sm text-amber-800 flex-1 min-w-0">
            Unsaved changes from your last visit were found
            {draftInfo.savedAt ? ` (${new Date(draftInfo.savedAt).toLocaleString("en-IN")})` : ""}.
          </p>
          <div className="flex items-center gap-2">
            <Btn type="button" variant="accent" onClick={restoreDraft} data-testid="pf-draft-restore">Restore</Btn>
            <Btn type="button" variant="outline" onClick={discardDraft} data-testid="pf-draft-discard">Discard</Btn>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-5 space-y-4">
            <Input label="Product Name" value={f.name} onChange={(e) => set("name", e.target.value)} testid="pf-name" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Select label="Category" value={f.categoryId} onChange={(e) => set("categoryId", e.target.value)} testid="pf-category">
                <option value="">Select category</option>
                {cats.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
              <Select label="Brand" value={f.brandId} onChange={(e) => set("brandId", e.target.value)} testid="pf-brand">
                <option value="">Select brand</option>
                {brands.data?.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Select>
            </div>
            <Input label="Short Description" value={f.shortDescription} onChange={(e) => set("shortDescription", e.target.value)} testid="pf-short" />
            <Textarea label="Full Description" rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} testid="pf-desc" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Warranty" value={f.warranty || ""} onChange={(e) => set("warranty", e.target.value)} testid="pf-warranty" />
              <Input label="Tags (comma separated)" value={Array.isArray(f.tags) ? f.tags.join(", ") : f.tags || ""} onChange={(e) => set("tags", e.target.value)} testid="pf-tags" />
            </div>
          </Card>

          {/* Images */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">Product Images</h3>
                <p className="text-xs text-slate-500">Upload JPG/PNG (max 10 MB) or add an image URL. The first image is the primary shown on cards.</p>
              </div>
            </div>

            <input ref={fileRef} type="file" accept="image/jpeg,image/png" multiple className="hidden" data-testid="pf-file-input"
              onChange={(e) => uploadFiles(e.target.files)} />
            <div className="flex flex-wrap gap-2 mb-4">
              <Btn type="button" variant="accent" onClick={() => fileRef.current?.click()} loading={uploading} data-testid="pf-upload-btn">
                <Upload className="h-4 w-4" /> Upload Images
              </Btn>
              <div className="flex gap-2 flex-1 min-w-[220px]">
                <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="https://image-url…" data-testid="pf-url-input"
                  className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-amber-400" />
                <Btn type="button" variant="outline" onClick={addUrl} data-testid="pf-add-url-btn"><Link2 className="h-4 w-4" /> Add URL</Btn>
              </div>
            </div>

            {f.images.length === 0 ? (
              <p className="text-xs text-slate-400">No images yet. Product cards use the primary image.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="pf-image-grid">
                {f.images.map((img, i) => (
                  <div key={i} className={`relative group rounded-lg border overflow-hidden ${i === 0 ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-200"}`} data-testid={`pf-image-${i}`}>
                    <img src={resolveMediaUrl(img)} alt="" onError={handleMediaError} className="h-24 w-full bg-slate-50 object-contain" />
                    {i === 0 && <span className="absolute top-1 left-1 bg-amber-500 text-slate-900 text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5"><Star className="h-2.5 w-2.5 fill-current" /> Primary</span>}
                    <div className="absolute inset-x-0 bottom-0 bg-slate-900/80 flex items-center justify-between px-1 py-1 opacity-0 group-hover:opacity-100 transition">
                      <button type="button" onClick={() => moveImg(i, -1)} disabled={i === 0} className="text-white/90 disabled:opacity-30 p-0.5" title="Move left"><ChevronLeft className="h-4 w-4" /></button>
                      {i !== 0 && <button type="button" onClick={() => setPrimary(i)} className="text-amber-300 p-0.5" title="Set as primary" data-testid={`pf-setprimary-${i}`}><Star className="h-4 w-4" /></button>}
                      <button type="button" onClick={() => removeImg(i)} className="text-red-300 p-0.5" title="Remove" data-testid={`pf-removeimg-${i}`}><Trash2 className="h-4 w-4" /></button>
                      <button type="button" onClick={() => moveImg(i, 1)} disabled={i === f.images.length - 1} className="text-white/90 disabled:opacity-30 p-0.5" title="Move right"><ChevronRight className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Variants */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-slate-900">Variants & Pricing</h3>
                <p className="text-xs text-slate-500">Add attributes (e.g. Colour, Length) or leave blank for a simple product. Weight (grams) is used for Delhivery shipping rates.</p>
              </div>
              <Btn type="button" variant="outline" onClick={() => set("variants", [...f.variants, emptyVariant()])} data-testid="add-variant-btn"><Plus className="h-4 w-4" /> Variant</Btn>
            </div>
            <div className="space-y-4">
              {f.variants.map((v, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3">
                  <div className="grid sm:grid-cols-4 gap-2">
                    <Input label="SKU" value={v.sku} onChange={(e) => setVariant(i, "sku", e.target.value)} testid={`variant-sku-${i}`} />
                    <Input label="Price (₹)" type="number" value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} testid={`variant-price-${i}`} />
                    <Input label="MRP (₹)" type="number" value={v.mrp} onChange={(e) => setVariant(i, "mrp", e.target.value)} testid={`variant-mrp-${i}`} />
                    <Input label="Stock" type="number" value={v.stock} onChange={(e) => setVariant(i, "stock", e.target.value)} testid={`variant-stock-${i}`} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 mt-2">
                    <div>
                      <Input label="Weight (grams)" type="number" min="1" max="100000" step="1" inputMode="numeric"
                        value={v.weightInput ?? ""} onChange={(e) => setVariantWeight(i, e.target.value)}
                        testid={`variant-weight-${i}`} />
                      {weightInputError(v.weightInput) ? (
                        <p className="text-xs text-red-600 mt-1" data-testid={`variant-weight-error-${i}`}>
                          {weightInputError(v.weightInput)}
                        </p>
                      ) : (
                        <p className="text-[11px] text-slate-400 mt-1" data-testid={`variant-weight-hint-${i}`}>{weightHint(v)}</p>
                      )}
                    </div>
                    <div className="flex items-end">
                      <p className="text-[11px] text-slate-400 pb-1">
                        Shipping weight of one unit of this variant. Delhivery charges are estimates and may differ
                        from the final courier invoice.
                      </p>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2 mt-2">
                    {[
                      ["lengthCm", "Length (cm)"],
                      ["breadthCm", "Breadth (cm)"],
                      ["heightCm", "Height (cm)"],
                    ].map(([field, label]) => {
                      const error = dimensionInputError(v[field], label.split(" ")[0]);
                      return (
                        <div key={field}>
                          <Input label={label} type="number" min="1" step="1" inputMode="numeric"
                            value={v[field] ?? ""} onChange={(e) => setVariant(i, field, e.target.value)}
                            testid={`variant-${field}-${i}`} />
                          {error ? <p className="text-xs text-red-600 mt-1" data-testid={`variant-${field}-error-${i}`}>{error}</p> : null}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Enter positive measurements of the actual packed parcel, including its packaging. Leave blank when not measured.</p>
                  <div className="grid sm:grid-cols-2 gap-2 mt-2">
                    <Input label="Attribute name (e.g. Colour)" value={Object.keys(v.attributes || {})[0] || ""}
                      onChange={(e) => { const val = Object.values(v.attributes || {})[0] || ""; setVariant(i, "attributes", e.target.value ? { [e.target.value]: val } : {}); }} />
                    <Input label="Attribute value (e.g. Black)" value={Object.values(v.attributes || {})[0] || ""}
                      onChange={(e) => { const key = Object.keys(v.attributes || {})[0] || "Variant"; setVariantAttr(i, key, e.target.value); }} />
                  </div>
                  {f.variants.length > 1 && (
                    <button type="button" onClick={() => set("variants", f.variants.filter((_, x) => x !== i))} className="text-xs text-red-600 mt-2 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove variant</button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Specs */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Specifications</h3>
              <Btn type="button" variant="outline" onClick={() => set("specifications", [...f.specifications, { key: "", value: "" }])}><Plus className="h-4 w-4" /> Add</Btn>
            </div>
            <div className="space-y-2">
              {f.specifications.map((s, i) => (
                <div key={i} className="flex gap-2">
                  <input value={s.key} onChange={(e) => { const arr = [...f.specifications]; arr[i] = { ...arr[i], key: e.target.value }; set("specifications", arr); }} placeholder="Key" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                  <input value={s.value} onChange={(e) => { const arr = [...f.specifications]; arr[i] = { ...arr[i], value: e.target.value }; set("specifications", arr); }} placeholder="Value" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                  <button type="button" onClick={() => set("specifications", f.specifications.filter((_, x) => x !== i))} className="p-2 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="p-5 space-y-3">
            <h3 className="font-semibold text-slate-900">Visibility</h3>
            <Select label="Status" value={f.status} onChange={(e) => set("status", e.target.value)} testid="pf-status">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
            <Input label="Low Stock Threshold" type="number" value={f.lowStockThreshold} onChange={(e) => set("lowStockThreshold", Number(e.target.value))} />
          </Card>
          <Card className="p-5 space-y-3">
            <h3 className="font-semibold text-slate-900">Merchandising</h3>
            <Toggle label="Featured" checked={f.featured} onChange={(v) => set("featured", v)} testid="pf-featured" />
            <Toggle label="Trending" checked={f.trending} onChange={(v) => set("trending", v)} testid="pf-trending" />
            <Toggle label="Best Seller" checked={f.bestSeller} onChange={(v) => set("bestSeller", v)} testid="pf-bestseller" />
            <Toggle label="New Arrival" checked={f.newArrival} onChange={(v) => set("newArrival", v)} testid="pf-newarrival" />
            <Toggle label="Has Variants (show selector)" checked={f.hasVariants} onChange={(v) => set("hasVariants", v)} testid="pf-hasvariants" />
          </Card>
          <Card className="p-5 space-y-3">
            <h3 className="font-semibold text-slate-900">GST &amp; Returns</h3>
            <Input label="HSN / SAC code" value={f.hsn || ""} onChange={(e) => set("hsn", e.target.value)} testid="pf-hsn" />
            <Input label="GST rate (%)" type="number" value={f.gstRate ?? ""} onChange={(e) => set("gstRate", e.target.value === "" ? null : Number(e.target.value))} testid="pf-gst-rate" />
            <Toggle label="Returns allowed" checked={!!f.returnPolicy?.allowed} onChange={(v) => set("returnPolicy", { ...(f.returnPolicy || {}), allowed: v })} testid="pf-returns-allowed" />
            <Input label="Return window (days)" type="number" value={f.returnPolicy?.windowDays ?? ""} onChange={(e) => set("returnPolicy", { ...(f.returnPolicy || {}), windowDays: e.target.value === "" ? null : Number(e.target.value) })} testid="pf-return-days" />
            <p className="text-[11px] text-slate-400">Leave the window blank to fall back to the store default return policy. Tax is always recalculated on the server.</p>
          </Card>
        </div>
      </div>
    </form>
  );
}
